import React, { createContext, useContext, useMemo } from 'react';
import { useBrowseFollow, type BrowseFollowKind } from './hooks/useBrowseFollow';
import type { BrowseUsageData } from './hooks/useBrowseUsage';

/** What every row needs to draw its cost chip and its Follow button. */
export interface BrowseRowContextValue {
  usage: BrowseUsageData | undefined;
  /** No credit is left: a row that costs one does not open (design b5). */
  outOfCredits: boolean;
  /** One credit is left: the chip says "Last credit" (design b7). */
  lastCredit: boolean;
  follow: (kind: BrowseFollowKind, id: string) => Promise<void>;
  unfollow: (kind: BrowseFollowKind, id: string) => Promise<void>;
  pendingId: string | null;
  /** The workspace follow slots of this kind are full (design b7 "5 of 5"). */
  slotsFull: (kind: BrowseFollowKind) => boolean;
  /** A click on a row that would cost a credit when none is left: show the credits. */
  onBlockedOpen: () => void;
  /** Called when the user opens a row: the query goes to the recent searches. */
  onOpenRow: () => void;
}

const BrowseRowContext = createContext<BrowseRowContextValue | null>(null);

interface BrowseRowProviderProps {
  usage: BrowseUsageData | undefined;
  onBlockedOpen: () => void;
  onOpenRow: () => void;
  children: React.ReactNode;
}

export const BrowseRowProvider: React.FC<BrowseRowProviderProps> = ({ usage, onBlockedOpen, onOpenRow, children }) => {
  const { follow, unfollow, pendingId } = useBrowseFollow();
  const value = useMemo<BrowseRowContextValue>(
    () => ({
      usage,
      outOfCredits: usage !== undefined && usage.left <= 0,
      lastCredit: usage !== undefined && usage.left === 1,
      follow,
      unfollow,
      pendingId,
      slotsFull: (kind) => {
        if (usage === undefined || kind === 'playlist') {
          return false;
        }
        const slots = kind === 'artist' ? usage.followSlots.artists : usage.followSlots.tracks;
        return slots.used >= slots.limit;
      },
      onBlockedOpen,
      onOpenRow,
    }),
    [usage, follow, unfollow, pendingId, onBlockedOpen, onOpenRow]
  );
  return <BrowseRowContext.Provider value={value}>{children}</BrowseRowContext.Provider>;
};

/** The row context. It throws outside a `BrowseRowProvider`. */
export function useBrowseRowContext(): BrowseRowContextValue {
  const value = useContext(BrowseRowContext);
  if (value === null) {
    throw new Error('useBrowseRowContext needs a BrowseRowProvider');
  }
  return value;
}
